import { ISOstring, TimeRange } from "@dataseat-dsp-client/lib-reporter";
import { FIGMA_COLORS } from "components-redesign/styles/colors";
import { endOfIsoDay, startOfIsoDay } from "helpers";
import { IRangePreset, getPresetsOptions } from "./presetsOptions";

export type ISODateArray = Array<ISOstring | null>;

export const getPreviousPresetUtc = (
  startPrevious: ISOstring | null,
  finishPrevious: ISOstring | null
): IRangePreset | null => {
  const presets = getPresetsOptions();
  if (!startPrevious && !finishPrevious)
    return presets[TimeRange.allTime] as IRangePreset;

  if (!startPrevious || !finishPrevious) return null;

  const quickLinkLabel = Object.keys(presets).find((link) => {
    if (!presets[link]?.value[0] || !presets[link]?.value[1]) return null;
    return (
      startOfIsoDay(presets[link]?.value[0]) === startOfIsoDay(startPrevious) &&
      endOfIsoDay(presets[link]?.value[1]) === endOfIsoDay(finishPrevious)
    );
  });

  return quickLinkLabel ? presets[quickLinkLabel] || null : null;
};

export function isoToDisplay(
  iso: ISOstring | null | undefined,
  includeTime = false
): string {
  if (!iso) return "";
  const datePart = iso.split("T")[0];
  const [y, m, d] = datePart.split("-");
  const dateStr = `${d}/${m}/${y}`;
  if (includeTime) {
    const timePart = iso.split("T")[1];
    if (timePart) {
      const [h, min] = timePart.split(":");
      return `${dateStr} ${h}:${min}`;
    }
  }
  return dateStr;
}

export function displayToIso(display: string): ISOstring | null {
  const match = display.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/
  );
  if (!match) return null;
  const [, d, m, y, h, min] = match;
  const day = parseInt(d, 10);
  const month = parseInt(m, 10);
  const year = parseInt(y, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900)
    return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return null;
  if (h !== undefined && min !== undefined) {
    const hour = parseInt(h, 10);
    const minute = parseInt(min, 10);
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return `${y}-${m}-${d}T${h}:${min}:00.000Z`;
  }
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

export function validateDateInput(
  display: string,
  minDate?: string,
  maxDate?: string,
  errorMessages?: { tooLate?: string; tooEarly?: string }
): string | null {
  if (!display) return null;
  const iso = displayToIso(display);
  if (!iso) return "Invalid format (DD/MM/YYYY)";
  if (maxDate && iso > maxDate)
    return errorMessages?.tooLate ?? "Date exceeds maximum";
  if (minDate && iso < minDate)
    return errorMessages?.tooEarly ?? "Date is before minimum";
  return null;
}

// Parses a "HH:MM", "HH:MM:SS" or "HH:MM:SS.mmm" default time into its parts.
export function parseDefaultTime(t?: string) {
  if (!t) return { hour: "00", minute: "00", secondsMs: "00.000" };
  const [hh = "00", mm = "00", ssRaw] = t.split(":");
  let secondsMs = "00.000";
  if (ssRaw) {
    const [s = "00", ms = "000"] = ssRaw.split(".");
    secondsMs = `${s.padStart(2, "0")}.${ms}`;
  }
  return {
    hour: hh.padStart(2, "0"),
    minute: mm.padStart(2, "0"),
    secondsMs,
  };
}

export function defaultTimeToIsoTime(t?: string): string {
  const { hour, minute, secondsMs } = parseDefaultTime(t);
  return `${hour}:${minute}:${secondsMs}`;
}

export const RANGE_ERRORS = {
  tooLate: "Date cannot be after end date",
  tooEarly: "Date cannot be before start date",
};

export const outlinedDateTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    fontSize: "14px",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6750A4",
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0, 0, 0, 0.23)",
    },
  },
  "& .MuiInputBase-input": {
    padding: "8px 12px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    color: FIGMA_COLORS.onSurfaceVariantLight,
    "&.Mui-focused": {
      color: "#6750A4",
      backgroundColor: "#ECE6F0",
      padding: "0 4px",
    },
    "&.Mui-error": {
      color: FIGMA_COLORS.onSurfaceVariantLight,
    },
  },
};

export const dateErrorTextSx = {
  color: "#FB8C00 !important",
  fontSize: "11px",
  lineHeight: "1.2",
};
